import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus,
  Upload,
  Settings,
  RefreshCw,
  Database,
} from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import FieldMappingEditor from '@/components/FieldMappingEditor';
import { api } from '@/services/api';

interface Connector {
  id: string;
  name: string;
  type: string;
  entityType: string;
  sourceSystem: string;
  status?: string;
  lastSyncAt?: string;
}

interface FileUploadResponse {
  fileName: string;
  size: number;
  preview: any[];
  totalRows: number;
  sourceFields: string[];
}

const IntegrationPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'connectors' | 'mappings' | 'history'>('connectors');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [selectedConnector, setSelectedConnector] = useState<Connector | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<FileUploadResponse | null>(null);
  const [currentMapping, setCurrentMapping] = useState<Record<string, string>>({});
  const queryClient = useQueryClient();

  // Fetch connectors
  const { data: connectors = [], isLoading: isLoadingConnectors } = useQuery<Connector[]>({
    queryKey: ['integration', 'connectors'],
    queryFn: async () => {
      const res = await api.get('/api/integration/connectors');
      return res.data.data;
    },
  });

  // Create file connector
  const createConnectorMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await api.post('/api/integration/connectors/file', payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Connector created');
      setShowCreateModal(false);
      queryClient.invalidateQueries({ queryKey: ['integration', 'connectors'] });
    },
    onError: () => {
      toast.error('Failed to create connector');
    },
  });

  // Upload file
  const uploadFileMutation = useMutation({
    mutationFn: async (formData: FormData) => {
      const res = await api.post(`/api/integration/connectors/${selectedConnector?.id}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      return res.data.data as FileUploadResponse;
    },
    onSuccess: (data) => {
      toast.success('File uploaded');
      setShowUploadModal(false);
      setSelectedFile(null);
      setUploadPreview(data);
      // Auto-open mapping modal
      setShowMappingModal(true);
    },
    onError: () => {
      toast.error('Failed to upload file');
    },
  });

  // Update mapping
  const updateMappingMutation = useMutation({
    mutationFn: async (payload: any) => {
      const res = await api.put(`/api/integration/connectors/${selectedConnector?.id}/mapping`, payload);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Mapping updated');
      setShowMappingModal(false);
    },
    onError: () => {
      toast.error('Failed to update mapping');
    },
  });

  // Run sync
  const syncMutation = useMutation({
    mutationFn: async (connectorId: string) => {
      const res = await api.post(`/api/integration/connectors/${connectorId}/sync`);
      return res.data;
    },
    onSuccess: () => {
      toast.success('Sync completed');
      queryClient.invalidateQueries({ queryKey: ['integration', 'connectors'] });
    },
    onError: () => {
      toast.error('Sync failed');
    },
  });

  const handleCreateConnector = (e: React.FormEvent) => {
    e.preventDefault();
    const form = e.target as HTMLFormElement;
    const payload = {
      name: (form.elements.namedItem('name') as HTMLInputElement).value,
      entityType: (form.elements.namedItem('entityType') as HTMLSelectElement).value,
      sourceSystem: (form.elements.namedItem('sourceSystem') as HTMLInputElement).value,
      fileType: (form.elements.namedItem('fileType') as HTMLSelectElement).value,
    };
    createConnectorMutation.mutate(payload);
  };

  const handleFileUpload = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile || !selectedConnector) return;
    const formData = new FormData();
    formData.append('file', selectedFile);
    uploadFileMutation.mutate(formData);
  };

  const handleUpdateMapping = () => {
    if (!selectedConnector || !currentMapping) return;
    updateMappingMutation.mutate({ fieldMappings: Object.entries(currentMapping).map(([target, source]) => ({ source, target, required: true })) });
    setShowMappingModal(false);
  };

  const getTargetFieldsForEntityType = (entityType: string) => {
    const fields: Record<string, Array<{ name: string; required: boolean; description?: string }>> = {
      product: [
        { name: 'name', required: true, description: 'Product name' },
        { name: 'sku', required: true, description: 'Product SKU/code' },
        { name: 'price', required: true, description: 'Selling price' },
        { name: 'description', required: false, description: 'Product description' },
        { name: 'category', required: false, description: 'Product category' },
        { name: 'stockQuantity', required: false, description: 'Current stock level' },
      ],
      customer: [
        { name: 'name', required: true, description: 'Customer name' },
        { name: 'email', required: false, description: 'Email address' },
        { name: 'phone', required: false, description: 'Phone number' },
        { name: 'taxId', required: false, description: 'Tax ID / TPIN' },
        { name: 'street', required: false, description: 'Street address' },
        { name: 'city', required: false, description: 'City' },
      ],
      invoice: [
        { name: 'number', required: true, description: 'Invoice number' },
        { name: 'date', required: true, description: 'Invoice date' },
        { name: 'customerId', required: false, description: 'Customer ID' },
        { name: 'total', required: true, description: 'Total amount' },
        { name: 'status', required: false, description: 'Invoice status' },
      ],
      payment: [
        { name: 'invoiceId', required: false, description: 'Related invoice ID' },
        { name: 'amount', required: true, description: 'Payment amount' },
        { name: 'method', required: false, description: 'Payment method' },
        { name: 'date', required: true, description: 'Payment date' },
        { name: 'reference', required: false, description: 'Payment reference' },
      ],
    };
    return fields[entityType] || [];
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Integration Hub</h1>
        <Button onClick={() => setShowCreateModal(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Connector
        </Button>
      </div>

      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {(['connectors', 'mappings', 'history'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-sm capitalize ${
                activeTab === tab
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {activeTab === 'connectors' && (
        <div className="space-y-4">
          {isLoadingConnectors ? (
            <div className="text-center py-8">Loading connectors...</div>
          ) : connectors.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Database className="h-12 w-12 mx-auto mb-4 text-gray-400" />
              No connectors configured
            </div>
          ) : (
            connectors.map((connector) => (
              <motion.div
                key={connector.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg border border-gray-200 p-6"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{connector.name}</h3>
                    <p className="text-sm text-gray-500">
                      {connector.type} • {connector.entityType} • {connector.sourceSystem}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedConnector(connector);
                        setShowUploadModal(true);
                      }}
                    >
                      <Upload className="h-4 w-4 mr-1" />
                      Upload
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedConnector(connector);
                        setShowMappingModal(true);
                      }}
                    >
                      <Settings className="h-4 w-4 mr-1" />
                      Map
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => syncMutation.mutate(connector.id)}
                      disabled={syncMutation.isPending}
                    >
                      <RefreshCw className={`h-4 w-4 mr-1 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                      Sync
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>
      )}

      {/* Create Connector Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-medium mb-4">Create File Connector</h2>
            <form onSubmit={handleCreateConnector}>
              <div className="space-y-4">
                <Input name="name" placeholder="Connector name" required />
                <select name="entityType" className="w-full p-2 border rounded" required>
                  <option value="">Select entity type</option>
                  <option value="product">Product</option>
                  <option value="customer">Customer</option>
                  <option value="invoice">Invoice</option>
                  <option value="payment">Payment</option>
                </select>
                <Input name="sourceSystem" placeholder="Source system name" required />
                <select name="fileType" className="w-full p-2 border rounded" required>
                  <option value="">Select file type</option>
                  <option value="csv">CSV</option>
                  <option value="xlsx">Excel</option>
                  <option value="json">JSON</option>
                </select>
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button type="button" variant="ghost" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={createConnectorMutation.isPending}>
                  Create
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload File Modal */}
      {showUploadModal && selectedConnector && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-lg font-medium mb-4">Upload File for {selectedConnector.name}</h2>
            <form onSubmit={handleFileUpload}>
              <div className="space-y-4">
                <input
                  type="file"
                  accept=".csv,.xlsx,.json"
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  className="w-full p-2 border rounded"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2 mt-6">
                <Button type="button" variant="ghost" onClick={() => setShowUploadModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={!selectedFile || uploadFileMutation.isPending}>
                  Upload
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Mapping Modal */}
      {showMappingModal && selectedConnector && uploadPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-medium mb-4">Field Mapping for {selectedConnector.name}</h2>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                File: {uploadPreview.fileName} ({uploadPreview.totalRows} rows)
              </p>
            </div>
            <FieldMappingEditor
              sourceFields={uploadPreview.sourceFields}
              targetFields={getTargetFieldsForEntityType(selectedConnector.entityType)}
              initialMapping={currentMapping}
              onChange={setCurrentMapping}
            />
            <div className="flex justify-end space-x-2 mt-6">
              <Button variant="ghost" onClick={() => setShowMappingModal(false)}>
                Close
              </Button>
              <Button onClick={handleUpdateMapping} disabled={updateMappingMutation.isPending}>
                Save Mapping
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationPage;
